"use client";

import {FormEvent, JSX, useState, useTransition} from "react";

import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";

import {verifyPassword} from "./actions";

export interface LoginFormProps {
    /**
     * SDK configuration key for password (e.g., "draft_password", "staging_password")
     * If provided, password will be retrieved from SDK configuration
     */
    passwordKey?: string;
    /**
     * Direct password value to verify against
     * If provided, this password will be used instead of passwordKey
     */
    expectedPassword?: string;
    /**
     * Custom cookie name for authentication
     * Defaults to "staging_auth" if not provided
     */
    cookieName?: string;
    /**
     * Custom title for the login form
     */
    title?: string;
    /**
     * Custom description for the login form
     */
    description?: string;
}

export function LoginForm({
    passwordKey,
    expectedPassword,
    cookieName,
    title = "Site Under Development",
    description = "Please enter the password to access this site.",
}: LoginFormProps): JSX.Element {
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isPending, startTransition] = useTransition();

    const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        setError("");

        startTransition(async () => {
            const isValid = await verifyPassword(password, {
                passwordKey,
                expectedPassword,
                cookieName,
            });

            if (isValid) {
                // Reload page to check auth status
                window.location.reload();
            } else {
                setError("Invalid password. Please try again.");
                setPassword("");
            }
        });
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f5f5f5",
            }}
        >
            <ContainerBase>
                <div
                    style={{
                        maxWidth: "400px",
                        margin: "0 auto",
                        padding: "40px",
                        backgroundColor: "#ffffff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    }}
                >
                    <Typography size={24} weight={600} color="title" as="h1" style={{marginBottom: "8px", textAlign: "center"}}>
                        {title}
                    </Typography>
                    <Typography size={16} color="title" as="p" style={{marginBottom: "24px", textAlign: "center"}}>
                        {description}
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <div style={{marginBottom: "16px"}}>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter password"
                                disabled={isPending}
                                required
                                style={{
                                    width: "100%",
                                    padding: "12px",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    boxSizing: "border-box",
                                }}
                            />
                        </div>

                        {error && (
                            <div
                                style={{
                                    marginBottom: "16px",
                                    padding: "12px",
                                    backgroundColor: "#fee",
                                    color: "#c33",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                }}
                            >
                                {error}
                            </div>
                        )}

                        <ButtonBase type="submit" variant="primary" size="md" width="full" disabled={isPending}>
                            {isPending ? "Verifying..." : "Enter"}
                        </ButtonBase>
                    </form>
                </div>
            </ContainerBase>
        </div>
    );
}
